import { useEffect, useState, useRef, useCallback, useMemo, MutableRefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Configs } from 'utils/constants';
import { isArray } from './common';
import { TailwindBreakpoint } from './frontendTypes';

export function useOnClickOutside(ref: MutableRefObject<any> | MutableRefObject<any>[], handler: (event: any) => void) {
  useEffect(() => {
    const listener = (event: any) => {
      const refs = isArray(ref) ? ref : [ref];
      for (let i = 0; i < refs.length; i += 1) {
        const elementRef = refs[i];
        // Do nothing if clicking ref's element or descendent elements
        if (!elementRef.current || elementRef.current.contains(event.target)) {
          return;
        }
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  options: { maxWait?: number; leading?: boolean } = {},
): [T, () => void, () => void] {
  const maxWait = options.maxWait;
  const maxWaitHandler = useRef(null);
  const maxWaitArgs: { current: any[] } = useRef([]);

  const leading = options.leading;
  const wasLeadingCalled: { current: boolean } = useRef(false);

  const functionTimeoutHandler = useRef(null);
  const isComponentUnmounted: { current: boolean } = useRef(false);

  const debouncedFunction = useRef(callback);
  debouncedFunction.current = callback;

  const cancelDebouncedCallback: () => void = useCallback(() => {
    clearTimeout(functionTimeoutHandler.current);
    clearTimeout(maxWaitHandler.current);
    maxWaitHandler.current = null;
    maxWaitArgs.current = [];
    functionTimeoutHandler.current = null;
    wasLeadingCalled.current = false;
  }, []);

  useEffect(
    () => () => {
      // we use flag, as we allow to call callPending outside the hook
      isComponentUnmounted.current = true;
    },
    [],
  );

  const debouncedCallback = useCallback(
    (...args) => {
      maxWaitArgs.current = args;
      clearTimeout(functionTimeoutHandler.current);

      if (!functionTimeoutHandler.current && leading && !wasLeadingCalled.current) {
        debouncedFunction.current(...args);
        wasLeadingCalled.current = true;
        return;
      }

      functionTimeoutHandler.current = setTimeout(() => {
        cancelDebouncedCallback();

        if (!isComponentUnmounted.current) {
          debouncedFunction.current(...args);
        }
      }, delay);

      if (maxWait && !maxWaitHandler.current) {
        maxWaitHandler.current = setTimeout(() => {
          const currentMaxWaitArgs = maxWaitArgs.current;
          cancelDebouncedCallback();

          if (!isComponentUnmounted.current) {
            debouncedFunction.current.apply(null, currentMaxWaitArgs);
          }
        }, maxWait);
      }
    },
    [maxWait, delay, cancelDebouncedCallback, leading],
  );

  const callPending = () => {
    // Call pending callback only if we have anything in our queue
    if (!functionTimeoutHandler.current) {
      return;
    }

    debouncedFunction.current.apply(null, maxWaitArgs.current);
    cancelDebouncedCallback();
  };

  // At the moment, we use 3 args array so that we save backward compatibility
  return [debouncedCallback as T, cancelDebouncedCallback, callPending];
}

// Uses a ref because that will stay up to date without having to re-render the component
export function useIsMountedRef(): { current: boolean } {
  const componentIsMounted = useRef(true);
  useEffect(
    () => () => {
      componentIsMounted.current = false;
    },
    [],
  );
  return componentIsMounted;
}

export const useIsMobile = () => {
  return useMediaQuery({ query: `(max-width: ${Configs.Breakpoints.Md - 1}px)` });
};

export const useTailwindBreakpoint = (): TailwindBreakpoint => {
  const tailwindBreakpointFromWidth = (width: number): TailwindBreakpoint => {
    if (width < 768) {
      return TailwindBreakpoint.SM;
    }
    if (width < 1024) {
      return TailwindBreakpoint.MD;
    }
    if (width < 1280) {
      return TailwindBreakpoint.LG;
    }
    if (width < 1536) {
      return TailwindBreakpoint.XL;
    }
    return TailwindBreakpoint.XXL;
  };

  const [tailwindBreakpoint, setTailwindBreakpoint] = useState<TailwindBreakpoint>(
    tailwindBreakpointFromWidth(window.innerWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      setTailwindBreakpoint(tailwindBreakpointFromWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  });

  return tailwindBreakpoint;
};

export const useResetPageScroll = (): void => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
};
